<template>
  <div v-bind="$attrs">
    <!-- Uploaded image preview -->
    <div
      v-if="!!preImageUrl"
      class="relative w-full rounded-lg overflow-hidden group"
      :style="{ aspectRatio: 1 / ratio }"
    >
      <img class="w-full h-full object-cover" :src="preImageUrl" alt="image to upload" />
      <div class="absolute inset-0 flex items-end justify-between gap-2 p-2 bg-gradient-to-t from-black/50 to-transparent">
        <button
          type="button"
          class="flex flex-row items-center justify-center gap-1 bg-white/90 text-black text-xs px-2 py-1 rounded-full backdrop-blur"
          @click="pickImage"
        >
          <i class="pi pi-camera" style="font-size: 0.75rem" />
          <span>החלפה</span>
        </button>
        <button
          type="button"
          class="flex flex-row items-center justify-center gap-1 bg-white/90 text-red-600 text-xs px-2 py-1 rounded-full backdrop-blur"
          @click="removeImage"
        >
          <i class="pi pi-trash" style="font-size: 0.75rem" />
          <span>הסרה</span>
        </button>
      </div>
    </div>

    <!-- Empty state / upload dropzone -->
    <div v-else class="flex items-center justify-center w-full" @click="pickImage">
      <div
        :style="{ aspectRatio: 1 / ratio }"
        class="flex flex-col items-center justify-center w-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
      >
        <div class="flex flex-col items-center justify-center p-4">
          <i class="pi pi-image text-gray-500 mb-2" style="font-size: 1.6rem" />
          <p class="mb-1 text-sm text-gray-500 dark:text-gray-400 font-semibold text-center">
            לחצו כדי להעלות תמונה
          </p>
          <p class="text-xs text-gray-400 dark:text-gray-500 text-center">
            WEBP, PNG, JPG or HEIF · עד 5 מ"ב
          </p>
        </div>
      </div>
    </div>

    <!-- Hidden web file input (kept out of clickable areas to avoid double-trigger) -->
    <input
      v-if="!isNative"
      ref="fileInputRef"
      type="file"
      class="hidden"
      accept="image/png, image/jpeg, image/webp"
      @change="selectPhotoWeb"
    />
  </div>

  <!-- Full-screen mobile-first crop modal -->
  <Teleport to="body">
    <div
      v-if="isCropping"
      class="fixed inset-0 z-[2000] flex flex-col bg-black"
      :style="{ paddingTop: safeAreaTop, paddingBottom: safeAreaBottom }"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <button type="button" class="text-base px-2 py-1" :disabled="uploadInProgress" @click="cancelCrop">
          ביטול
        </button>
        <span class="font-medium">חיתוך תמונה</span>
        <button
          type="button"
          class="flex items-center gap-2 text-base font-semibold px-3 py-1 rounded-full bg-primary text-white disabled:opacity-60"
          :disabled="uploadInProgress"
          @click="crop"
        >
          <i v-if="uploadInProgress" class="pi pi-spinner pi-spin" style="font-size: 0.9rem" />
          <span>{{ uploadInProgress ? 'שומר...' : 'שמירה' }}</span>
        </button>
      </div>

      <!-- Cropper canvas -->
      <div class="flex-1 min-h-0">
        <cropper
          ref="cropperRef"
          class="w-full h-full"
          :src="preview"
          :stencil-props="{ aspectRatio: 1 / ratio, movable: true, resizable: true }"
          image-restriction="fit-area"
        />
      </div>

      <p class="text-center text-white/70 text-sm py-3 shrink-0">
        גררו וצבטו כדי להתאים את התמונה
      </p>
    </div>
  </Teleport>
</template>

<script lang="ts">
export default { inheritAttrs: false };
</script>

<script lang="ts" setup>
import { computed, PropType, ref, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { SharedApi } from '@app/api/admin';
import { ICoordinates } from '@torim-app/types';
import { downscaleImage, MAX_UPLOAD_IMAGE_DIMENSION } from '@torim-app/utils';
import { useToast } from 'vue-toastification';
import { useSharedStore } from '@torim-app/shared-components';

const props = defineProps({
  uploadFunction: {
    type: Function,
    required: true
  },
  imageUrl: {
    type: String,
    required: false
  },
  origin: {
    type: String as PropType<'bg' | 'logo' | 'service' | 'profile' | 'shop' | 'gallery'>,
    required: true
  },
  ratio: {
    type: Number,
    default: 1
  }
});
const emit = defineEmits(['remove']);

const toast = useToast();
const sharedStore = useSharedStore();

const cropperRef = ref<typeof Cropper>();
const fileInputRef = ref<HTMLInputElement>();
const preview = ref<string>();
const selectedImage = ref<Blob>();
const uploadInProgress = ref(false);
const isCropping = ref(false);
const coordinates = ref<ICoordinates>();
const preImageUrl = ref(props.imageUrl);

const isNative = computed(() => Capacitor.isNativePlatform());
const safeAreaTop = computed(() => sharedStore.getInset.topPadding);
const safeAreaBottom = computed(() => sharedStore.getInset.bottomPadding);

const uploadImage = async () => {
  try {
    if (selectedImage.value) {
      const formData = new FormData();
      formData.append('file', selectedImage.value);
      return await SharedApi.uploadImages(formData, coordinates.value);
    }
  } catch (e) {
    console.error(e);
    // The image is scaled down before it gets here, so a failure now is almost always the
    // network dropping the request — blaming the image sent owners hunting for a smaller
    // photo when retrying was what they needed.
    throw 'העלאת התמונה נכשלה, בדקו את החיבור לאינטרנט ונסו שוב';
  }
};

const crop = async () => {
  const result = cropperRef.value?.getResult();
  coordinates.value = result?.coordinates;
  uploadInProgress.value = true;

  try {
    const uploadedImageUrl = await uploadImage();
    if (uploadedImageUrl?.length) {
      preImageUrl.value = uploadedImageUrl[0];
      props.uploadFunction(uploadedImageUrl[0]);
      closeCropper();
    }
  } catch (e) {
    console.error(e);
    toast.error(<string>e);
  } finally {
    uploadInProgress.value = false;
  }
};

const pickImage = () => {
  if (isNative.value) selectPhotoFromGallery();
  else fileInputRef.value?.click();
};

/**
 * Scales the picked image down before anything else sees it.
 *
 * Deliberately ahead of the cropper: the crop coordinates the server extracts with are in
 * this blob's pixel space, so the image being cropped has to be the one being uploaded.
 */
const openCropper = async (blob: Blob) => {
  const sized = await downscaleImage(blob);
  if (preview.value) URL.revokeObjectURL(preview.value);
  preview.value = URL.createObjectURL(sized);
  selectedImage.value = sized;
  isCropping.value = true;
};

const closeCropper = () => {
  isCropping.value = false;
  if (preview.value) {
    URL.revokeObjectURL(preview.value);
    preview.value = undefined;
  }
  selectedImage.value = undefined;
};

const cancelCrop = () => {
  if (uploadInProgress.value) return;
  closeCropper();
};

const selectPhotoFromGallery = async () => {
  // Ask the native picker to hand back a bounded image rather than the full 12MP original.
  // `openCropper` enforces the bound again for platforms that ignore these.
  const images = await Camera.pickImages({
    quality: 90,
    limit: 1,
    width: MAX_UPLOAD_IMAGE_DIMENSION,
    height: MAX_UPLOAD_IMAGE_DIMENSION,
  });
  const imagePath = images.photos.length > 0 ? images.photos[0].webPath : null;
  if (imagePath) {
    const blob = await fetch(imagePath).then((r) => r.blob());
    await openCropper(blob);
  }
};

const selectPhotoWeb = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const blob = target.files?.length ? target.files[0] : null;
  // reset so re-selecting the same file re-triggers change
  target.value = '';
  if (blob) await openCropper(blob);
};

const removeImage = () => {
  preImageUrl.value = undefined;
  emit('remove');
};

watch(
  () => props.imageUrl,
  () => {
    preImageUrl.value = props.imageUrl;
  }
);

// Allows hosts to render their own trigger (e.g. an avatar with a pencil badge)
// while reusing the pick → crop → upload flow.
defineExpose({ pickImage });
</script>

<style scoped></style>
